import { useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { img, PROJECTS, LOREM } from "@/lib/site";

export default function ProjectDetail() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug) || PROJECTS[0];
  const index = PROJECTS.findIndex((p) => p.slug === project.slug);
  const next = PROJECTS[(index + 1) % PROJECTS.length];

  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const facts = [
    { label: "Location", value: "Copenhagen, DK" },
    { label: "Discipline", value: project.category },
    { label: "Year", value: project.year },
    { label: "Scope", value: "Supply · Specification · Sampling" },
  ];

  return (
    <>
      {/* Hero image */}
      <section ref={ref} className="relative h-[86svh] overflow-hidden" data-testid="project-hero">
        <motion.img
          src={img(project.cover, 2400)}
          alt={project.title}
          style={{ y, scale: 1.12 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <div className="relative z-10 flex h-full flex-col justify-end pb-14 edge text-background">
          <span className="eyebrow text-background/70">{project.category} · {project.year}</span>
          <h1 className="mt-4 text-6xl font-light tracking-editorial md:text-8xl">
            {project.title}
          </h1>
        </div>
      </section>

      {/* Overview */}
      <section className="edge py-24 md:py-32" data-testid="project-overview">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <dl className="divide-y hairline border-t hairline">
              {facts.map((f) => (
                <div key={f.label} className="flex items-baseline justify-between py-4">
                  <dt className="eyebrow">{f.label}</dt>
                  <dd className="text-sm tracking-editorial">{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div className="space-y-6 md:col-span-7 md:col-start-6">
            <p className="text-2xl font-light leading-snug tracking-editorial md:text-3xl">
              A considered residence where our materials set the tone from floor to
              ceiling.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              For this project we supplied engineered timber flooring, veneer panels
              and natural stone — working alongside the design team to sample,
              specify and deliver every surface on schedule. The result is a home
              that feels cohesive, warm and built to last.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="edge pb-24 md:pb-32" data-testid="project-gallery">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          {project.gallery.map((g, i) => (
            <Reveal
              key={i}
              delay={(i % 2) * 0.08}
              className={i % 3 === 0 ? "md:col-span-2" : ""}
            >
              <div className="overflow-hidden">
                <img
                  src={img(g, i % 3 === 0 ? 2000 : 1200)}
                  alt={`${project.title} detail ${i + 1}`}
                  className={`w-full object-cover ${i % 3 === 0 ? "aspect-[16/9]" : "aspect-[4/5]"}`}
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Next project */}
      <section className="border-t hairline" data-testid="project-next">
        <Link to={`/portfolio/${next.slug}`} className="group block edge py-16" data-testid="project-next-link">
          <span className="eyebrow">Next Project</span>
          <div className="mt-3 flex items-center justify-between">
            <h2 className="text-4xl font-light tracking-editorial md:text-6xl">{next.title}</h2>
            <span className="text-2xl transition-transform duration-500 group-hover:translate-x-2">→</span>
          </div>
        </Link>
      </section>
    </>
  );
}
